import React, { useState, useEffect } from 'react';
import './DateRangePicker.scss';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import { I18n } from 'react-redux-i18n';
import moment from 'moment';

const DateRangePicker = (props) => {
    const [allLastRegisteredDates, setAllLastRegisteredDates] = useState(undefined);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [loadedReceivedDates, setLoadedReceivedDates] = useState(false);

    useEffect(() => {
        registerLocale('es', es);
        setDefaultLocale('es');
        getAllLastRegisteredDates();
    }, []);

    useEffect(() => {
        if (props.receiveDates && !loadedReceivedDates) {
            setStartDate(props.receiveDates.startDate);
            setEndDate(props.receiveDates.endDate);
            setLoadedReceivedDates(true);
        }
    }, [props.receiveDates]);

    // Update allLastRegisteredDates when allTransactions changes (screen switching)
    useEffect(() => {
        getAllLastRegisteredDates();

        // Only clear date selection when switching between different transaction types
        // (volume vs normal), not when the same screen updates its data
        if (props.allTransactions && props.allTransactions.length > 0) {
            const currentTransactionType = props.allTransactions[0].patient ? 'normal' : 'volume';
            const previousType = window.lastTransactionType || null;

            if (previousType && previousType !== currentTransactionType) {
                console.log(
                    '🔍 DateRangePicker: Transaction type changed from',
                    previousType,
                    'to',
                    currentTransactionType,
                    '- clearing dates',
                );
                setStartDate(undefined);
                setEndDate(undefined);
            }

            window.lastTransactionType = currentTransactionType;
        }
    }, [props.allTransactions]);

    const getAllLastRegisteredDates = () => {
        let allLastRegisteredDates = [];
        if (props.allTransactions && Array.isArray(props.allTransactions)) {
            props.allTransactions.map((transaction) => {
                allLastRegisteredDates.push(
                    Array.isArray(transaction.dates)
                        ? transaction.dates[transaction.dates.length - 1].created_at
                        : transaction.timeReceived || transaction.updated_at,
                );
            });
        }
        setAllLastRegisteredDates(allLastRegisteredDates);
    };

    const handleDateChangeRaw = (e) => {
        e.preventDefault();
    };

    const handleStartDateChange = (selectedStartDate) => {
        console.log('🔍 DateRangePicker: Start date changed to:', selectedStartDate);
        setStartDate(selectedStartDate);
        //only user roche
        if (props.startDate) {
            const formattedDate = moment(selectedStartDate).format('YYYY-MM-DD');
            console.log('🔍 DateRangePicker: Calling props.startDate with:', formattedDate);
            props.startDate(formattedDate);
            props.setPage(0);
        }

        // Call callback if provided
        if (props.onDateRangeChange) {
            console.log(
                '🔍 DateRangePicker: Calling onDateRangeChange with:',
                selectedStartDate,
                endDate,
            );
            props.onDateRangeChange(selectedStartDate, endDate);
        }

        filterDates(selectedStartDate, endDate);
        document.getElementById('end-date-react-picker').focus();
    };

    const handleEndDateChange = (selectedEndDate) => {
        console.log('🔍 DateRangePicker: End date changed to:', selectedEndDate);
        setEndDate(selectedEndDate);
        //only user roche
        if (props.endDate) {
            const formattedDate = moment(selectedEndDate).format('YYYY-MM-DD');
            console.log('🔍 DateRangePicker: Calling props.endDate with:', formattedDate);
            props.endDate(formattedDate);
            props.setPage(0);
        }

        // Call callback if provided
        if (props.onDateRangeChange) {
            console.log(
                '🔍 DateRangePicker: Calling onDateRangeChange with:',
                startDate,
                selectedEndDate,
            );
            props.onDateRangeChange(startDate, selectedEndDate);
        }

        filterDates(startDate, selectedEndDate);
    };

    const filterDates = (selectedStartDate, selectedEndDate) => {
        if (selectedStartDate && props.allTransactions && Array.isArray(props.allTransactions)) {
            const datesInRange = getDatesInRange(
                selectedStartDate,
                selectedEndDate ? selectedEndDate : new Date(), // Use today as end date if not provided
            );
            let transactionsInRange = props.allTransactions.filter((obj) =>
                datesInRange.includes(
                    obj.dates
                        ? obj.dates[obj.dates.length - 1].created_at
                        : obj.timeReceived || obj.updated_at,
                ),
            );
            if (props.textToSearch) {
                props.updateFilteredTransactionsByDateRange(transactionsInRange);
                props.searchText(props.textToSearch);
            } else {
                props.updateFilteredTransactionsByDateRange(transactionsInRange);
            }
        }
    };

    const getDatesInRange = (selectedStartDate, selectedEndDate) => {
        let datesInRange = [];
        const endDateFormatted = moment(
            moment(selectedEndDate).format('YYYY-MM-DD') + ' ' + '23:59',
        );
        allLastRegisteredDates.map((date) => {
            if (
                moment(date).isBetween(selectedStartDate, endDateFormatted, undefined, '[]') ||
                (selectedStartDate === endDateFormatted &&
                    moment(date).format('L') === moment(selectedStartDate).format('L'))
            ) {
                datesInRange.push(date);
            }
        });
        return datesInRange;
    };

    const clearDatesSelection = () => {
        props.clearDateRangeSelection();
        setStartDate(undefined);
        setEndDate(undefined);

        // Call callback if provided
        if (props.onDateRangeChange) {
            props.onDateRangeChange(undefined, undefined);
        }
    };

    return (
        <div className='date-range-picker'>
            {props.startDateLabel && <label>{props.startDateLabel}</label>}
            <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                onChangeRaw={handleDateChangeRaw}
                placeholderText={I18n.t(
                    'rocheTransaction.dateRangePicker.startDatePickerPlaceholder',
                )}
                maxDate={moment().endOf('day').toDate()}
                autoComplete='off'
                locale='es'
                showMonthYearPicker={props.showMonthYearPicker}
                dateFormat={props.showMonthYearPicker ? 'MMM yyyy' : 'dd/MM/yyyy'}
            />
            {props.endDateLabel && <label>{props.endDateLabel}</label>}
            <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                onChangeRaw={handleDateChangeRaw}
                placeholderText={I18n.t(
                    'rocheTransaction.dateRangePicker.endDatePickerPlaceholder',
                )}
                minDate={startDate}
                maxDate={moment().endOf('day').toDate()}
                autoComplete='off'
                locale='es'
                id='end-date-react-picker'
                showMonthYearPicker={props.showMonthYearPicker}
                dateFormat={props.showMonthYearPicker ? 'MMM yyyy' : 'dd/MM/yyyy'}
            />
            {(startDate || endDate) && !props.notShowReset && (
                <button
                    className='clear-dates-selection-button'
                    type='submit'
                    title={I18n.t(
                        'rocheTransaction.dateRangePicker.clearDatesSelectionButtonTooltip',
                    )}
                >
                    <i
                        className='icon-close clear-dates-selection-icon'
                        onClick={clearDatesSelection}
                    />
                </button>
            )}
        </div>
    );
};

DateRangePicker.propTypes = {
    allTransactions: PropTypes.array.isRequired,
    textToSearch: PropTypes.string.isRequired,
    searchText: PropTypes.func.isRequired,
    updateFilteredTransactionsByDateRange: PropTypes.func.isRequired,
    clearDateRangeSelection: PropTypes.func.isRequired,
    startDate: PropTypes.func,
    endDate: PropTypes.func,
    setPage: PropTypes.func,
    showMonthYearPicker: PropTypes.bool,
    receiveDates: PropTypes.object,
    notShowReset: PropTypes.bool,
    startDateLabel: PropTypes.string,
    endDateLabel: PropTypes.string,
    onDateRangeChange: PropTypes.func,
};

export default DateRangePicker;
